import { ErrorBase } from '@models/classes/error-base.model';
import { AttachmentCloudUploadStatus } from '@models/enums/attachment-cloud-upload-status.enum';
import { type SyncAction } from '@models/enums/db-query-action.enum';
import { PerformAuditAttachmentLocation } from '@models/enums/perform-attachment-location.enum';
import { type PerformSyncState } from '@models/enums/perform-sync-state.enum';
import { VisitDbSyncTaskStatus } from '@services/perform/perform-idb/backbone-idb.types';
import { BreadcrumbItem } from '@syncfusion/ej2-angular-navigations';


export type SqlBool = 1 | 0;

export type AuditList = {
    audit_no?: string;
    audit_type?: string;
    category?: string;
    completed?: string;
    form_completed?: number,
    attempted?: number
}
export type AuditDetails = {
    account_manager?: string;
    audit_status?: string;
    acc_code?: string;
    acc_name?: string;
    audit_category?: string;
    audit_no?: string;
    audit_type?: string;
    auditor?: string;
    duration?: string;
    facility_address?: string;
    spl_instructions?: string;
    visit_no?: string;
    audit_note?: string;
    isAttempted?: number;
};

/**
 * Select name with un answered questions count
 */
export type SectionName = {
    /**Audit section seq */
    seq: string;
    section_seq: number;
    section_name: string;
    un_answered_ques_count: number;
    template_seq: number;
    visible: boolean;
    na_flg: boolean | number;
    na_allowed: boolean | number;
    col?: string;
    canBeRepeated?: SqlBool;
    canBeEdited?: SqlBool;
};

export type QuestionAnsType = 'RADIO' | 'CHECKBOX' | 'DATE' | 'NARRATIVE' | 'TEMP' | 'NUMERIC';

export type NoteDataType = 'DATE' | 'NARRATIVE' | 'TEMP' | 'NUMERIC';

export type QuestionItem = {
    parentQuesSeq?: number;
    ques_seq: number;
    att_count: number;
    audit_no: string;
    audit_sec_seq: string;
    section_seq: number;
    section_name: string;
    audit_ques_seq: string;
    question_id: string;
    question: string;
    ques_answer: string | Date | number;
    answer_type: QuestionAnsType;
    visible: boolean;
    template_seq: number;
    POLICY_COUNT: number;
    // policy_id: string;
    // policy_name: string;
    // policy_text: string;
    answers?: AnswerType[];
    trigger_ans_seq: string | null;
    ca_flg: number,
    critical_flg: number,
    na: number,
    card_color?: string,
    notes: string,
    pdn_count: number,
    trigger_ques_count: number,
    mandatory_notes?: boolean
};

export type AnswerType = {
    ans_seq: number;
    answer: string;
    answer_type: QuestionAnsType;
    question_seq: number;
    template_seq: number;
};

export type SpecialInstruction = {
    spl_instructions: string;
};

export type Attachment = {
    seq?: string | null;
    attachment_type: string;
    attachment_name: string;
    description: string | null;
    object_type: string | null;
    object_ref: string | null;
    attachment_path?: string | null | null;
    attachmentFileMimeType?: string | null;
    device_attachment_path: string | null | null;
    doc_sensitivity: string | null;
    download_url: string | null;
    download_url_expiry_date: Date | null | null;
    upload_url: string | null;
    upload_url_expiry_date: Date | null | null;
    cloud_upload_status: AttachmentCloudUploadStatus | null;
    active_flag: boolean | null;
    create_date: Date;
    create_user: string;
    modify_date: Date | null;
    modify_user: string | null;
    sync_action: string | null;
    attachmentWebUrl?: string | null;
    fileSize?: number | null;
    attachment_display_type?: string
};

export type questionItemData = {
    ques_seq: number | undefined;
    audit_ques_seq: string | undefined;
    pdn_count: number;
};

export type AllPDNotes = {
    seq: string;
    ques_seq: string;
    display_seq: string;
    parent_pdn_seq: string;
    note_id: string;
    note: string;
    score: number;
    note_comment: string;
    selected?: boolean;
};

export type SelectedPDNotes = {
    seq: string;
    audit_ques_seq: string | undefined;
    pdn_seq: string;
    create_date: Date;
    create_user: string;
    modify_date: Date;
    modify_user: string;
    additional_notes: string;
    sync_action: string;
};
export type AttachmentNotUploaded = {
    seq: string;
    auditNo?: string;
    visitNo: string;
    objectType: string;
    objectRef: string;
    fileName: string;
    attachmentType: string;
};

export type Notes = {
    notes: string | Date | number;
    notes_type: NoteDataType;
};

export type NASection = {
    section_seq: string;
    section_name: string;
    audit_sec_na_flg: number;
};

export type PolicyData = {
    policy_seq: number | undefined;
    policy_id: string | undefined;
    policy_name: string | undefined;
    policy_text: string | undefined;
};

export type GeneralTabInfo = {
    sectionName: string;
    answered_questions: number;
    total_questions: number;
    score: number;
    grade: number;
};

export type OverallInfo = {
    answered_questions: number;
    total_questions: number;
    final_score: number;
    audit_result: string;
};

export type QuestionDetails = {
    section_seq: number;
    template_seq: number;
    SEC_DSP_ID: string;
    ADD_SECTION_NAME: string;
    QUES_DSP_ID: string;
    QUES_TEXT: string;
    ques_seq: number;
    NOTES: string;
    answer: string;
    score: string;
    AUSQ_SEQ: string;
    ca_flg: string;
    parent_ques_seq: string;
    PN_cnt: number;
    PDN_ARR: PN_DATA[] | null;
    ATTACH_cnt: number;
    ATTACH_ARR: ATTACH_DATA[] | null;
    trigger_cnt: number;
    TRiGGER_ARR: QuestionDetails[] | null;
};

export type PN_DATA = {
    dsp_seq_nbr: number;
    note_id: string;
    note_text: string;
    addl_notes: string;
}
export type ATTACH_DATA = {
    ID: string;
    attachment: string;
    attachment_name: string;
    attachment_src: string | File;
    attachmentWebUrl?: string;
}

export type UnansweredQuestionDetails = {
    section_name: string;
    question_id: string;
    question: string;
    ques_seq: number;
};
export type AuditValidation = {
    audit_number: string;
    audit_name: string;
    template_seq: number;
    section_name: string;
    section_seq: string;
    question_seq: number;
    audit_ques_seq: string;
    message: string;
};

export type SubmitFormType = {
    contact_name: string;
    startDate: Date;
    startTime: Date;
    endDate: Date;
    endTime: Date;
    signatureImage: string;
    facilityCode: string;
    auditNo: number;
    DURATION_NBR: number;
};

export type UpSyncVisitReq = {
    visitNo: string;
    user: string;

    deviceId: string;

    requestId: string;

    tables: Array<{
        tableName: string;

        data: Array<any>;
    }>;
};

export type SectionDetails = {
    audit_sec_seq: string;
    sectionName: string;
    question: string;
    question_id: string;
    answer: string;
    notes: string;
    attachment_path: string;
    pdn_notes: string;
    pdn_addn_notes: string;
    max_score: string;
    score: string;
    repeat_count: number;
    attachments?: Attachment[];
};

export type SectionDetailsGrouped = {
    [key: string]: SectionDetails[]
};


export type AuditDetailsObj = {
    /**
     * String value to display as key on left side
     */
    [key: string]: string | null | number | string[];
};

export type SectionWiseData = {
    /**
     * String value to display as key on left side
     */
    [key: string]: SectionDetails[];
};

export type FetchSubmitFormData = {
    fac_audit_contact_name: string;
    audit_note: string;
    actual_start_date: string;
    actual_end_date: string;
    status: string;
    facility_seq: string;
    audit_no: string;
    duration: number;
    duration_unit: string;
    ATTACHMENT_CNT: number;
};

export type SignatureInfo = {
    seq: string,
    attachment_name: string,
    attachment_path: string,
    attachment_src: string | File
}

export type GeocodeInfo = {
    seq?: string;
    object_type?: string;
    object_ref?: string;
    latitude: number | undefined;
    longitude: number | undefined;
    method?: string;
    create_date: string;
    create_user: string;
    modify_date: string;
    modify_user: string;
    sync_action: SyncAction;
    sync_status: PerformSyncState;
};

export type ValidationMessageType = {
    auditNumber: string;
    auditName: string;
    templateSeq: number;
    audit_ques_seq?: string;
    section: Section[] | undefined;
};

export type Section = {
    sectionSeq: string;
    sectionName: string;
    validation: Validation[];
};

export type Validation = {
    text: string;
    questionSeq: number;
    audit_ques_seq?: string;
};

export type Geolocation = {
    lat: number;
    long: number;
};

/**
 * Attachment download url request DTO
 */
export type AttDownloadUrlReqDto = {
    seq: string;
    attachmentPath: string;
    attachmentName: string;
    attachmentType: string;
    visitNo: string;
    auditNo?: string;
};

export type AttDownloadUrlResDto = {
    seq: string;
    auditNo: string | null;
    attachmentName: string;
    visitNo: string | null;
    auditQuestionSeq: string;
    downloadUrl: string;
    downloadUrlLifeTime: string;
};

export type _BreadcrumbItem = Partial<BreadcrumbItem> & {
    txt_code?: string,
    title?: string
}

/**
 * Contact request DTO
 */
export type ContactDto = {
    seq?: string;
    acc_seq?: string;
    salutation: string;
    first_name: string;
    middle_name: string;
    last_name: string;
    email: string;
    alternate_email: string;
    phone: string;
    alternate_phone: string;
    same_as_customer: number;
    address: string;
    city: string;
    is_primary?: string;
    address_type?: string;
    state_code?: string;
    cou_code?: string,
    sync_action?: string,
    sync_status?: string,
    create_date?: Date;
    create_user?: string;
    modify_date: Date;
    modify_user: string;
};

export enum VisitStatus { CREATED = 'CREATED', COMPLETED = 'COMPLETED', SUBMITTED = 'SUBMITTED' }


export type InsertDeleteRecordsOptions = { id_name: string, id_value: string | string[], table_name: string, visit_no: string, audit_no: string | null, action: SyncAction, sync_status: PerformSyncState }


export type DeleteRecords =
    {
        id_name: string,
        id_value: string,
        table_name: string,
        visit_no: string,
        audit_no: string,
        action: SyncAction,
        sync_status: PerformSyncState
    }

export type DeleteRecordUpSyncTableData = {
    [key: string]: string,
    sync_action: SyncAction,
    sync_status: PerformSyncState
}

export type DeleteRecordUpSync = {
    tableName: string,
    data: DeleteRecordUpSyncTableData[]
}

export enum PerformSyncResult {
    Success = 'success',
    Failed = 'failed',
    In_Progress = 'in-progress',
}


export type PerformContact = {
    seq: number,
    acc_seq: number,
    address_type: string,
    last_name: string,
    middle_name: string,
    first_name: string,
    salutation: string,
    same_as_customer: number,
    address: null | string,
    email: string,
    city: null | string,
    state_code: null | string,
    cou_code: null | string,
    alternate_email: null | string,
    phone: string,
    alternate_phone: null | string,
    is_primary: number,
    create_date: string,
    create_user: string,
    modify_date: string,
    modify_user: null | string,
    sync_action: null | SyncAction,
    sync_status: null | PerformSyncState,
}

export type PerformSyncLiveTask = {
    visitNo: string,
    description: string,
    status: VisitDbSyncTaskStatus,
    error?: ErrorBase
}
export type PerformSyncLiveStatus = {
    taskList: PerformSyncLiveTask[],
    description: string,
    status: PerformSyncState,
    error?: ErrorBase
}

export type insertAttachmentOptions = {
    visitNo: string,
    auditNo?: string,
    attachments: File[],
    targetLocation: PerformAuditAttachmentLocation,
    objectRef: string,
    description: string,
    attachment_type: string,
}

export type CopySectionRecord = {
    audit_no: string,
    section_seq: string,
}

export interface InsertCopySectionRecord extends CopySectionRecord {
    seq: string, section_name: string
}

export type CopyQuesRecord = {
    seq: string,
    audit_no: string,
    question_seq: string,
}


export interface InsertCopyQuestionRecord extends CopyQuesRecord {
    audit_section_seq: string, 
}

export type SecSeq = { seq : string }

export type QuestionDetailsQuesList = {
    auditQuesSeq: string;
    quesSeq:string;
    templateSeq: number;
    auditSecSeq: string;
    secSeq: string;
}

